import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import type { UserManagerSettings, User as OidcUser } from 'oidc-client-ts';
import processProxy from '@/env/processProxy';
import { createCustomCookieStorage, getCookie } from '@/services/utils/CookieStorage';
import { crossTabRenewCoordinator } from '@/services/auth/CrossTabRenewCoordinator';

const ZITADEL_COOKIE_NAME = 'oidc.storage' as const;

const requiredEnvVars = {
  VUE_APP_ZITADEL_AUTHORITY: processProxy.VUE_APP_ZITADEL_AUTHORITY,
  VUE_APP_ZITADEL_CLIENT_ID: processProxy.VUE_APP_ZITADEL_CLIENT_ID,
  VUE_APP_ZITADEL_PASSWORD_CHANGE_URL: processProxy.VUE_APP_ZITADEL_PASSWORD_CHANGE_URL,
} as const;

const missingVars = Object.entries(requiredEnvVars)
  .filter(([_, value]) => !value)
  .map(([key]) => key);

if (missingVars.length > 0) {
  throw new Error(`Missing required environment variables: ${missingVars.join(', ')}`);
}

const baseUrl = window.location.origin;
const isLendiDomain = baseUrl.includes('lendi');

const AUTHORITY_URL = processProxy.VUE_APP_ZITADEL_AUTHORITY;
const CLIENT_ID = processProxy.VUE_APP_ZITADEL_CLIENT_ID;
const REDIRECT_URI = isLendiDomain ? `${baseUrl}/v2/auth/callback` : `${baseUrl}/auth/callback`;
const POST_LOGOUT_REDIRECT_URI = isLendiDomain ? `${baseUrl}/v2/auth/logout/callback` : `${baseUrl}/auth/logout/callback`;


// Module-level singleton instance
let userManagerInstance: UserManager | null = null;
let automaticRenewPromise: Promise<OidcUser | null> | null = null;
let resolveAutomaticRenewPromise: ((user: OidcUser | null) => void) | null = null;
let automaticRenewLockId: string | null = null;
let stopAutomaticRenewHeartbeat: (() => void) | null = null;
type SignInPrompt = 'none' | 'login' | 'consent' | 'select_account';

const startAutomaticRenewTracking = () => {
  if (automaticRenewPromise) {
    return;
  }

  automaticRenewPromise = new Promise<OidcUser | null>(resolve => {
    resolveAutomaticRenewPromise = resolve;
  });

  if (!automaticRenewLockId) {
    const lockResult = crossTabRenewCoordinator.acquireLock();
    if (lockResult.acquired && lockResult.lockId) {
      automaticRenewLockId = lockResult.lockId;
      stopAutomaticRenewHeartbeat = crossTabRenewCoordinator.startHeartbeat(automaticRenewLockId);
    }
  }
};

const finishAutomaticRenewTracking = (user: OidcUser | null) => {
  if (resolveAutomaticRenewPromise) {
    resolveAutomaticRenewPromise(user);
  }

  automaticRenewPromise = null;
  resolveAutomaticRenewPromise = null;

  if (automaticRenewLockId) {
    if (user?.access_token) {
      crossTabRenewCoordinator.publishSuccess(automaticRenewLockId);
    } else {
      crossTabRenewCoordinator.publishFailure(automaticRenewLockId);
    }
    stopAutomaticRenewHeartbeat?.();
    crossTabRenewCoordinator.releaseLock(automaticRenewLockId);
  }

  automaticRenewLockId = null;
  stopAutomaticRenewHeartbeat = null;
};

export const useZitadelAuth = () => {
  // Initialize the UserManager instance if it hasn't been created yet
  if (!userManagerInstance) {
    const customCookieStorage = createCustomCookieStorage(ZITADEL_COOKIE_NAME, [
      'refresh_token',
      'token_type',
      'expires_at',
      'access_token',
    ]);

    const oidConfig: UserManagerSettings = {
      authority: AUTHORITY_URL as string,
      client_id: CLIENT_ID as string,
      redirect_uri: REDIRECT_URI as string,
      post_logout_redirect_uri: POST_LOGOUT_REDIRECT_URI as string,
      response_type: 'code',
      scope: 'openid offline_access profile email',
      response_mode: 'fragment',
      filterProtocolClaims: true,

      // Primary renewal path; 401 handler uses a guarded manual fallback only when needed.
      silent_redirect_uri: REDIRECT_URI,
      automaticSilentRenew: true,
      accessTokenExpiringNotificationTimeInSeconds: 60,
      metadata: {
        issuer: AUTHORITY_URL,
        authorization_endpoint: `${AUTHORITY_URL}oauth/v2/authorize`,
        token_endpoint: `${AUTHORITY_URL}oauth/v2/token`,
        userinfo_endpoint: `${AUTHORITY_URL}oidc/v1/userinfo`,
        end_session_endpoint: `${AUTHORITY_URL}oidc/v1/end_session`,
        jwks_uri: `${AUTHORITY_URL}.well-known/jwks.json`,
      },
      loadUserInfo: false,
      userStore: new WebStorageStateStore({ store: customCookieStorage, }),
    };

    userManagerInstance = new UserManager(oidConfig);
    userManagerInstance.events.addAccessTokenExpiring(() => {
      startAutomaticRenewTracking();
    });
    userManagerInstance.events.addUserLoaded((user: OidcUser) => {
      finishAutomaticRenewTracking(user);
    });
    userManagerInstance.events.addSilentRenewError(() => {
      finishAutomaticRenewTracking(null);
    });
  }

  /** @Info Retrieves token from zitadel. !!!Has to be performed on the Client side!! */
  const tokenCallback = async (): Promise<OidcUser> => {
    return await userManagerInstance!.signinRedirectCallback();
  };

  /** @Info Redirect to zitadel login page */
  const signingInRedirect = async (userName?: string, prompt?: SignInPrompt) => {
    const signInArgs: { login_hint?: string; prompt?: SignInPrompt } = {};
    const loginHint = userName?.trim();
    if (loginHint) {
      signInArgs.login_hint = loginHint;
    }
    if (prompt) {
      signInArgs.prompt = prompt;
    }

    await userManagerInstance!.signinRedirect(
      Object.keys(signInArgs).length > 0 ? signInArgs : undefined,
    );
  };

  const signingInRedirectSelectAccount = async () => {
    await signingInRedirect(undefined, 'select_account');
  };

  const logoutRedirect = async (args?: { idTokenHint?: string; logoutHint?: string; }) => {
    await userManagerInstance!.signoutRedirect({
      id_token_hint: args?.idTokenHint,
      extraQueryParams: args?.logoutHint ? { logout_hint: args.logoutHint } : undefined,
    });
  };

  /** @Info triggered by refresh token logic, user loaded events, and any actions that change oidcUser state */
  const onTokenObtaining = (action: Function) => {
    userManagerInstance!.events.addUserLoaded((user: OidcUser) => {
      action(user);
    });
  }

  const isAutomaticRenewInProgress = () => !!automaticRenewPromise;

  const waitForAutomaticRenew = () => automaticRenewPromise;

  /**
   * Checks whether the oidc.storage cookie contains a stored OIDC session with a refresh token.
   * Returns true even when the access token cookie has expired, as long as silent renewal is possible.
   */
  const hasStoredOidcSession = (): boolean => {
    const stored = getCookie(ZITADEL_COOKIE_NAME);
    if (!stored || typeof stored !== 'object') return false;
    return Object.values(stored).some(
      (v: any) => v && typeof v === 'object' && v.refresh_token
    );
  };

  return {
    tokenCallback,
    signingInRedirect,
    signingInRedirectSelectAccount,
    logoutRedirect,
    userManager: userManagerInstance,
    onTokenObtaining,
    isAutomaticRenewInProgress,
    waitForAutomaticRenew,
    hasStoredOidcSession,
  };
};
